import orderBy from "lodash/orderBy";
import { v4 as uuidv4 } from "uuid";
import { STUDIO_DATA_TYPE } from "@/static/global-variables";
import DateUtil from "@/utils/date-util";
import LocaleUtil from "@/utils/locales-util";
import store from "@/store";
import i18n from "@/plugins/i18n";

class DistributionChannelUtil {
  constructor() {
    this.prop = Object.freeze({
      defaultZipFileName: "CustomDisplayElement",
      field: "field",
      private: "private",
      all: "all",
      none: "none",
      allLabel: i18n.t("all")
    });

    this.studioDataTypeMapping = Object.freeze({
      string: STUDIO_DATA_TYPE.STRING,
      number: STUDIO_DATA_TYPE.NUMBER,
      studiodate: STUDIO_DATA_TYPE.DATE,
      boolean: STUDIO_DATA_TYPE.BOOLEAN,
      studiodatetime: STUDIO_DATA_TYPE.DATE_TIME
    });

    this.memberTemplate = Object.freeze({
      type: {
        text: ""
      },
      name: "",
      kind: this.prop.field,
      custom: true
    });

    this.propTemplate = Object.freeze({
      type: {
        text: ""
      },
      name: "",
      kind: this.prop.field,
      parent: "",
      custom: true
    });
  }

  formatPublished(published) {
    const formatted = published.map(e => {
      e.formattedPublished = DateUtil.formatDateTimeFromISO(
        e.published,
        store.state.userLocale,
        store.state.userTimeZone
      );
      return this.splitSemanticVersion(e);
    });
    return formatted;
  }

  formatAndSortSemanticVersion(versions) {
    const formatted = versions.map(e => {
      return this.splitSemanticVersion({ ...e });
    });
    return this.sortByVersion(formatted);
  }

  splitSemanticVersion(e) {
    const arr = e.version.split(".");
    e.major = Number(arr[0]);
    e.minor = Number(arr[1]);
    const patchTagArr = arr[2].split("-");
    e.patch = Number(patchTagArr[0]);
    if (patchTagArr.length > 1) {
      patchTagArr.shift();
      e.tag = patchTagArr.join("-");
    }
    return e;
  }

  sortByVersion(versions) {
    return orderBy(
      versions,
      ["major", "minor", "patch"],
      ["desc", "desc", "desc"]
    );
  }

  formatWidgetVersions(widgetuuid, data) {
    // create one array and sort to display on UI
    const published = data.published || [];
    let unpublished = data.unpublished || [];
    const versions = this.formatPublished(published);
    const orderedVersions = this.sortByVersion(versions);
    return {
      widgetuuid,
      versions: [...unpublished, ...orderedVersions]
    };
  }

  formatWidgetPublishedVersions(uuid, widgetuuid, data) {
    // only published versions
    const rawPublised = data.published || [];
    const rawFormatted = this.formatPublished(rawPublised);
    const published = this.sortByVersion(rawFormatted);
    const versions = [
      ...[this.prop.allLabel],
      ...published.map(e => e.version)
    ];
    return {
      uuid,
      widgetuuid,
      published,
      versions
    };
  }

  formatWidgetTranslations(widgetuuid, data) {
    return {
      widgetuuid,
      translations: data
    };
  }

  formatLatestWidgetsObject(uuid, latestWidgets) {
    return {
      uuid,
      widgets: this.orderListByName(latestWidgets)
    };
  }

  orderListByName(list) {
    return orderBy(list || [], [e => e.name.toLowerCase()], ["asc"]);
  }

  formatWidgetVersionDetails(widgetversionuuid, data) {
    return {
      widgetversionuuid,
      details: data
    };
  }

  countMatchedTranslations(memberList, translation) {
    let total = 0;
    if (Object.keys(translation).length) {
      for (const m of memberList) {
        const name = this.formatDisplayName(m.name);
        if (translation[name] && translation[name].message) {
          total++;
        }
      }
    }
    return total;
  }

  addArrayFields(member) {
    /*
      There are object entries with type: Arrays. For example:
      [
        {
          "type": {
            "text": "Array<{start: date, active: boolean}>"
          },
          "description": "Users start date and active status",
          "name": "users",
          "kind": "field"
        },
        {
          "type": {
            "text": "Array<{text: string, completed: boolean}>"
          },
          "description": "The todos to show in this widget",
          "name": "todos",
          "kind": "field"
        }
      ]
      Both should be expanded with their properties added in the object
      with their name as @[object.name]_[array.property]
      Example: for "users" with two properties in the entries, "start" and "active" should be:
      @@users_start and @users_active
      Those properties endup displaying in the UI.

    */
    let text = member.type.text.trim().replace(/Array[ ]{0,}<[ ]{0,}{/i, ""); // remove start
    text = text.replace(/}[ ]{0,}>[ ]{0,}/, "").trim(); // remove end
    const arr = text.split(",");
    const props = [];
    for (const ar of arr) {
      const parts = ar.split(":");
      const property = parts[0].trim();
      const type = parts[1].trim();
      const formatName = this.formatDisplayName(member.name);
      const name = `@${formatName}_${property}`;
      const prop = {
        type: {
          text: type
        },
        name: `${name}`,
        displayName: `${name}`,
        kind: this.prop.field,
        parent: member.name,
        property: property
      };
      props.push(prop);
    }
    member.props = props;
  }

  formatDisplayName(name) {
    return name && this.isOptional(name) ? name.slice(0, -1) : name;
  }

  isOptional(name) {
    return name.endsWith("?");
  }

  isFieldMember(member) {
    return Boolean(
      member &&
        member.kind === this.prop.field &&
        member.privacy !== this.prop.private
    );
  }

  formatWidgetVersionMembers(members) {
    const list = [];
    for (const member of members) {
      if (this.isFieldMember(member)) {
        member.displayName = this.formatDisplayName(member.name);
        member.required = !this.isOptional(member.name);
        member.type.text = member.type.text.trim();
        if (this.isMemberFieldTypeArray(member.type.text)) {
          // is Array dataType
          member.isArray = true;
          // format array fields as members to show on UI
          this.addArrayFields(member);
        }
        list.push(member);
      }
    }
    return list;
  }

  isMemberFieldTypeArray(text) {
    return text.match(/Array[ ]{0,}<[ ]{0,}{/i);
  }

  flattenMemberList(formattedMembers) {
    return formattedMembers.flatMap(e => {
      let props;
      if (e.isArray) {
        props = [e, ...e.props];
      }
      return props ? props : [e];
    });
  }

  isValidCustomDisplayElmentInfo(ctx) {
    let isValid = false;
    if (ctx) {
      const isEditingTranlations = ctx.translations.some(e => e.isEdit);
      const isAddingNewTranslation = Boolean(ctx.newTranslation);
      const hasSelectedFile = Boolean(ctx.file);
      const hasWidgetName = Boolean(ctx.widgetName);
      const hasValidPrereleaseLabel = this.isValidPrereleaseLabel(
        ctx.preReleaseLabel
      );
      isValid =
        !isEditingTranlations &&
        !isAddingNewTranslation &&
        hasSelectedFile &&
        hasWidgetName &&
        hasValidPrereleaseLabel;
    }
    return isValid;
  }

  isValidPrereleaseLabel(preReleaseLabel) {
    return Boolean(
      !preReleaseLabel ||
        (!preReleaseLabel.match(/^(0)/) &&
          !preReleaseLabel.match(/^(-)/) &&
          !preReleaseLabel.match(/^(\.)/) &&
          !preReleaseLabel.match(/[^a-zA-Z0-9.-]|[.-]{2}|[-.]\1/g))
    );
  }

  formatPostDataVerifyZip(widgetData) {
    const formdata = new FormData();
    if (widgetData.file) {
      formdata.append("file", widgetData.file, widgetData.file.name);
      if (widgetData.widgetuuid) {
        formdata.append("uuid", widgetData.widgetuuid);
      }
    }
    return formdata;
  }

  localizeUploadErrorMessage(errorResponse, defaultResponse) {
    let errorMsg = defaultResponse;
    const localizedMsg = i18n.t(errorResponse.code);
    if (localizedMsg) {
      errorMsg = localizedMsg;
      const tokens = localizedMsg.match(/\{([^{}]+)\}/g);
      if (tokens) {
        errorMsg = this.replaceErrorMsgs(
          localizedMsg,
          tokens,
          errorResponse.parameters
        );
      }
    }
    return errorMsg;
  }

  replaceErrorMsgs(localizedMsg, tokens, parameters) {
    let index = 0;
    if (tokens) {
      for (const token of tokens) {
        const paramVal = parameters[index++];
        const nextLocalizedMsg = i18n.t(paramVal);
        if (nextLocalizedMsg !== paramVal) {
          const nextTokens = nextLocalizedMsg.match(/\{([^{}]+)\}/g);
          const newMsg = this.replaceErrorMsgs(
            nextLocalizedMsg,
            nextTokens,
            parameters.slice(index)
          );
          localizedMsg = localizedMsg.replace(token, newMsg || "");
        } else {
          localizedMsg = localizedMsg.replace(token, paramVal || "");
        }
      }
    }
    return localizedMsg;
  }

  formatPostDataNewWidget(widgetData) {
    const formdata = new FormData();
    if (widgetData && widgetData.file && widgetData.widgetName) {
      const file = widgetData.file;
      const translations = widgetData.translations;

      const formatted = translations.map(e => {
        return { language: e.language, label: e.label };
      });
      const formattedString = JSON.stringify(formatted);
      const blob = new Blob([formattedString], { type: "application/json" });
      const preReleaseString =
        widgetData.preReleaseLabel || widgetData.unpublished;

      formdata.append("file", file, file.name);
      formdata.append("translations", blob, "translations");
      formdata.append("name", widgetData.widgetName);
      formdata.append("preReleaseString", preReleaseString);
    }
    return formdata;
  }

  getDownloadVersionName(widgets, versions, widgetuuid, widgetversionuuid) {
    let widgetName = this.prop.defaultZipFileName;
    let version;
    const widget = widgets ? widgets.find(e => e.uuid === widgetuuid) : null;
    if (widget) {
      widgetName = widget.name;
      // widget version may be within the list of latest widgets
      // in either the current or upublished verison
      if (widget.current && widget.current.uuid === widgetversionuuid) {
        version = widget.current.version;
      } else if (
        widget.unpublished &&
        widget.unpublished.uuid === widgetversionuuid
      ) {
        version = widget.unpublished.version;
      }
    }
    if (!version && versions) {
      const widgetverision = versions.find(e => e.uuid === widgetversionuuid);
      version = widgetverision ? widgetverision.version : null;
    }
    return version ? `${widgetName}_${version}.zip` : `${widgetName}.zip`;
  }

  getDisplayVersion(ctx) {
    return ctx && ctx.version
      ? `${ctx.version}-${ctx.preReleaseLabel || ctx.unpublished}`
      : "";
  }

  isValidNewVersionCustomDisplayElmentInfo(ctx) {
    return Boolean(
      ctx && ctx.file && this.isValidPrereleaseLabel(ctx.preReleaseLabel)
    );
  }

  formatPostDataNewWidgetVersion(widgetData) {
    const formdata = new FormData();
    if (widgetData && widgetData.file) {
      const preReleaseString =
        widgetData.preReleaseLabel || widgetData.unpublished;

      formdata.append("file", widgetData.file, widgetData.file.name);
      formdata.append("preReleaseString", preReleaseString);
      formdata.append("version", widgetData.version);
    }
    return formdata;
  }

  isUnpublished(versions, widgets, widgetuuid, widgetversionuuid) {
    let isUnpublished = false;
    const version = versions.find(e => e.uuid === widgetversionuuid);
    if (version) {
      isUnpublished = !version.published;
    } else {
      const widget = widgets.find(e => e.uuid === widgetuuid);
      isUnpublished = Boolean(
        widget &&
          widget.unpublished &&
          widget.unpublished.uuid === widgetversionuuid
      );
    }
    return isUnpublished;
  }

  formatCustomerDomainList(uuid, data) {
    return {
      uuid,
      customers: this.orderListByName(data || [])
    };
  }

  isValidCustomerDomainInfo(ctx) {
    return Boolean(ctx && ctx.domain);
  }

  doesCustomerDomainExistForChannel(customerDomain, domainArray) {
    const domain = domainArray.find(
      element =>
        element.name.toLowerCase() === customerDomain.domain.toLowerCase()
    );
    return domain !== undefined;
  }

  setupWidgetAccess(selectedAccess, access) {
    selectedAccess[access.uuid] = {
      uuid: access.uuid,
      name: access.name,
      version: access.version,
      prevVersion: access.prevVersion,
      update: access.update
    };
    return {
      customer: access.customer,
      access: selectedAccess
    };
  }

  formatCustomerAccessPayload(uuid, customerAccess) {
    const fullAccess = customerAccess.fullAccess;
    const widgetAccess = this.formatWidgetAccess(fullAccess, customerAccess);
    return {
      uuid,
      payload: {
        fullAccess,
        widgetAccess
      }
    };
  }

  formatWidgetAccess(fullAccess, customerAccess) {
    if (!fullAccess) {
      return Object.values(customerAccess).flatMap(e => {
        let access;
        if (e.uuid) {
          let widgetVersion = e.version;
          if (widgetVersion !== this.prop.none) {
            widgetVersion =
              widgetVersion && widgetVersion.split(".").length > 2
                ? widgetVersion
                : this.prop.all;
          }
          access = {
            widgetId: e.uuid,
            version: widgetVersion
          };
        }
        return access ? [access] : [];
      });
    }
  }

  resetCustomerAccessFlags(customerAccess) {
    customerAccess.update = false;
    customerAccess.prevFullAccess = customerAccess.fullAccess;
    for (const access of Object.values(customerAccess)) {
      if (access.uuid) {
        if (customerAccess.fullAccess) {
          access.prevVersion = this.prop.allLabel;
          access.version = this.prop.allLabel;
        } else {
          access.prevVersion = access.version;
        }
        access.update = false;
      }
    }
    return customerAccess;
  }

  removeWidgetsWithOnlyUnpublishedVersion(widgets) {
    return widgets.filter(e => Boolean(e.current));
  }

  getTranslatedLabel(translations, locale) {
    return translations.find(
      e => e.language.toLowerCase() === locale.toLowerCase()
    );
  }

  getTranslatedLabelAnyLongFormat(translations, locale) {
    return translations.find(e =>
      e.language.toLowerCase().startsWith(locale.toLowerCase())
    );
  }

  getTranslatedLabelLongFormat(translations, dashFormat, underscoreFormat) {
    let translation = this.getTranslatedLabel(translations, dashFormat);
    if (!translation) {
      translation = this.getTranslatedLabel(translations, underscoreFormat);
    }
    return translation;
  }

  findTranslatedLabel(translations, locale) {
    let translation;
    if (translations) {
      // try to find translation with current user locale
      translation = this.getTranslatedLabel(translations, locale);
      if (!translation) {
        const variants = LocaleUtil.geLocaleVariations(locale);
        if (variants.dashFormat) {
          // userLocale might be in long format such as "en-US" or "en_US"
          translation = this.getTranslatedLabelLongFormat(
            translations,
            variants.dashFormat,
            variants.underscoreFormat
          );
        }
        if (!translation) {
          // try to find label using the locale in short format such as "en"
          translation = this.getTranslatedLabel(translations, variants.short);
        }
        if (!translation) {
          // Try to find best match of long format variation
          translation = this.getTranslatedLabelAnyLongFormat(
            translations,
            variants.short
          );
        }
      }
    }
    return translation ? translation.label : null;
  }

  isValidCustomDisplayElementConfig(widgetData) {
    let invalidProperties = true;
    let invalidArrayProperties = false;
    let invalidCustomMembers = false;
    let invalidCustomMemberExpressions = false;
    let invalidCustomMemberFields = false;
    let hasDuplicateCustomMembers = false;
    let hasDuplicateMemberFields = false;
    if (widgetData && Object.keys(widgetData).length) {
      invalidProperties = Object.keys(widgetData.properties).some(key => {
        const e = widgetData.properties[key];
        return (
          !this.isOptional(key) &&
          !this.isMemberFieldTypeArray(e.type) &&
          !e.expression
        );
      });

      invalidArrayProperties = Object.keys(widgetData.properties).some(key => {
        const e = widgetData.properties[key];
        return (
          this.isMemberFieldTypeArray(e.type) &&
          !this.isOptional(key) &&
          e.fields &&
          Object.values(e.fields).some(e => !e)
        );
      });

      invalidCustomMembers = widgetData.customMembers.members.some(
        e => !e.type.text || !e.name
      );
      invalidCustomMemberExpressions = Object.values(
        widgetData.customMembers.properties
      ).some(e => !e.expression);

      const customMemberNames = widgetData.customMembers.members.map(
        e => e.name
      );
      hasDuplicateCustomMembers =
        customMemberNames.length !== new Set(customMemberNames).size;

      invalidCustomMemberFields = Object.values(
        widgetData.customMemberFields
      ).some(mf => {
        return Object.values(mf).some(
          el => !el.memberProp.name || !el.expression
        );
      });

      for (const memberField of Object.values(widgetData.customMemberFields)) {
        const fieldNames = Object.values(memberField).map(
          el => el.memberProp.name
        );
        hasDuplicateMemberFields =
          fieldNames.length !== new Set(fieldNames).size;
        if (hasDuplicateMemberFields) {
          break;
        }
      }
    }
    return (
      !invalidProperties &&
      !invalidArrayProperties &&
      !invalidCustomMembers &&
      !invalidCustomMemberExpressions &&
      !invalidCustomMemberFields &&
      !hasDuplicateCustomMembers &&
      !hasDuplicateMemberFields
    );
  }

  parseWidgetVersions(versionList) {
    let versions = [];
    if (versionList) {
      const formatted = this.formatAndSortSemanticVersion(versionList);
      versions = formatted.map(e => e.version);
    }
    return versions;
  }

  findWidgetVersionUuid(versionList, version) {
    let versionObj;
    if (versionList) {
      versionObj = versionList.find(e => e.version === version);
    }
    return versionObj ? versionObj.uuid : null;
  }

  /*
  The input is based on the data type of the field:

  Field Data Type       input
  ---------------       ------------------------
  string	              Global String Expression
  number	              Global Number Expression
  StudioDate	          Global Date Expression
  boolean	              Global Bool Expression
  Array<{object}>       A two stage input:
                        - First, it shows an input that selects a "List of Records" data element.
                        - Second, it shows one input per field in the object.
                              * Each input has a translated label
                                (message key = "@{{field name}}_{{object's field name}}")
                              * And a Local Expression input. The data type of the Local Expression
                                input matches the data type of this field in the object.
                                These inputs are Local to the data element selected in the first stage.
  */
  getDataType(member) {
    let dataType;
    if (member) {
      if (this.isMemberFieldTypeArray(member.type.text)) {
        dataType = member.type.text;
      } else {
        dataType = this.studioDataTypeMapping[member.type.text.toLowerCase()];
      }
    }
    return dataType;
  }

  findWidgetTranslations(translations, locale) {
    let objTranslatios = translations[locale];
    if (!objTranslatios) {
      const variants = LocaleUtil.geLocaleVariations(locale);
      objTranslatios =
        translations[variants.dashFormat] ||
        translations[variants.underscoreFormat] ||
        translations[variants.short];
    }
    return objTranslatios;
  }

  memberMessageTranslation(translations, member) {
    let message = member.displayName;
    const translation =
      translations[member.name] || translations[member.displayName];
    if (translation) {
      message = translation.message;
    }
    return message;
  }

  isValidMember(member) {
    return member && member.type && member.type.text && member.name;
  }

  initProperties(properties, membersList) {
    for (const member of membersList) {
      if (member.isArray) {
        this.setupArray(properties, member);
      } else {
        this.setupField(properties, member);
      }
    }
  }

  setupField(properties, member) {
    properties[member.name] = {
      type: member.type.text,
      expression: null
    };
  }

  setupArray(properties, member) {
    properties[member.name] = {
      type: member.type.text,
      dataSource: "",
      fields: this.setupArrayFields(member)
    };
  }

  setupArrayFields(member) {
    const fields = {};
    for (const prop of member.props) {
      fields[prop.property] = null;
    }
    return fields;
  }

  addNewCustomMember(widgetData, name, existingCustomMember) {
    const newMember = JSON.parse(JSON.stringify(this.memberTemplate));
    newMember.customId = uuidv4();
    newMember.name = name || "";
    newMember.type.text = existingCustomMember ? existingCustomMember.type : "";
    widgetData.customMembers.properties[newMember.customId] = {
      type: existingCustomMember ? existingCustomMember.type : "",
      expression: existingCustomMember ? existingCustomMember.expression : null
    };
    widgetData.customMembers.members.push(newMember);
  }

  createNewCustomProp(member, fieldName, existingExpression) {
    const newProp = JSON.parse(JSON.stringify(this.propTemplate));
    newProp.customId = uuidv4();
    newProp.parent = member.name;
    newProp.name = fieldName ? fieldName : "";
    newProp.type.text = existingExpression ? existingExpression.dataType : "";
    return newProp;
  }

  getAllProperties(widgetData) {
    const allProperties = { ...widgetData.properties };
    const keys = Object.keys(allProperties);
    for (let k of keys) {
      if (this.isOptional(k)) {
        let newKey = k.replace("?", "");
        allProperties[newKey] = allProperties[k];
        delete allProperties[k];
      }
    }
    const members = widgetData.customMembers.members || [];
    const properties = widgetData.customMembers.properties || {};
    // add custom members
    for (const member of members) {
      allProperties[member.name] = {
        type: properties[member.customId].type,
        expression: properties[member.customId].expression
      };
    }
    // add custom fields of existing members
    for (const memberCustomProps of Object.values(
      widgetData.customMemberFields
    )) {
      for (const customProp of Object.values(memberCustomProps)) {
        const property = allProperties[customProp.memberProp.parent];
        property.fields[customProp.memberProp.name] = customProp.expression;
      }
    }
    return allProperties;
  }

  inversePropertyNameRequirement(name) {
    if (name.endsWith("?")) {
      return name.slice(0, -1);
    } else {
      return `${name}?`;
    }
  }

  removeProperties(newVersionProperties, oldVersionProperties) {
    for (const propertyName of Object.keys(oldVersionProperties)) {
      if (!(
          newVersionProperties[propertyName] ||
          newVersionProperties[this.inversePropertyNameRequirement(propertyName)]
        )) {
        delete oldVersionProperties[propertyName];
      }
    }
  }

  setupExistingProperties(widgetData, membersList) {
    const newManifestProperties = {}; // To set initial properties from the manifest file
    this.initProperties(newManifestProperties, membersList);
    const currentProperties = widgetData.properties;

    if (widgetData.shouldUpdateProperties) {
      // If major version is changed, the properties that are not in the lower version should be removed
      this.removeProperties(newManifestProperties, currentProperties);
    }

    for (const currentProperty of Object.keys(currentProperties)) {
      const optionalCurrentProperty = `${currentProperty}?`;
      const newManifestContainsOptionalCurrentProperty =
        newManifestProperties[optionalCurrentProperty];
      const newManifestContainsRequiredCurrentProperty =
        newManifestProperties[currentProperty];
      if (
        newManifestContainsRequiredCurrentProperty ||
        newManifestContainsOptionalCurrentProperty
      ) {
        // Member comes from manifest file
        if (
          this.isMemberFieldTypeArray(currentProperties[currentProperty].type)
        ) {
          let currentPropertyBecameOptional =
            optionalCurrentProperty in newManifestProperties;
          this.setupExistingArrayProperties(
            newManifestProperties,
            currentProperty,
            currentProperties,
            widgetData,
            currentPropertyBecameOptional
          );
        } else {
          //standard field type
          const info = {
            ...newManifestProperties[currentProperty],
            ...currentProperties[currentProperty]
          };
          newManifestProperties[optionalCurrentProperty] = info;
          newManifestProperties[currentProperty] = info;
        }
      } else {
        // this is a custom member
        this.addNewCustomMember(
          widgetData,
          currentProperty,
          currentProperties[currentProperty]
        );
      }
    }
    widgetData.properties = newManifestProperties;
  }

  setupExistingArrayProperties(
    newManifestProperties,
    currentProperty,
    currentProperties,
    widgetData,
    currentPropertyBecameOptional
  ) {
    let newManifestPropertyName = currentProperty;
    if (currentPropertyBecameOptional) {
      newManifestPropertyName = `${currentProperty}?`;
    }
    newManifestProperties[newManifestPropertyName].dataSource =
      currentProperties[currentProperty].dataSource;
    // get all fields to find out which ones are from manifest or custom added
    const initialFields = Object.keys(
      newManifestProperties[newManifestPropertyName].fields
    );
    for (const fieldName of Object.keys(
      currentProperties[currentProperty].fields
    )) {
      if (initialFields.includes(fieldName)) {
        // field comes from manifest file
        newManifestProperties[newManifestPropertyName].fields[fieldName] =
          currentProperties[currentProperty].fields[fieldName];
      } else {
        // this is a custom field
        const existingExpression =
          currentProperties[currentProperty].fields[fieldName];
        const newProp = this.createNewCustomProp(
          { name: currentProperty },
          fieldName,
          existingExpression
        );
        const customFields =
          widgetData.customMemberFields[currentProperty] || {};
        customFields[newProp.customId] = {
          memberProp: newProp,
          expression: existingExpression
        };
        widgetData.customMemberFields[currentProperty] = customFields;
      }
    }
  }

  filterMembers(members) {
    return members ? members.filter(member => this.isFieldMember(member)) : [];
  }

  isUpgradeVersion(currentVersion, selectedVersion) {
    let isNewVersion = false;
    if (selectedVersion && currentVersion) {
      isNewVersion = selectedVersion.major > currentVersion.major;
      if (!isNewVersion) {
        isNewVersion = selectedVersion.minor > currentVersion.minor;
      }
      if (!isNewVersion) {
        isNewVersion = selectedVersion.patch > currentVersion.patch;
      }
    }
    return isNewVersion;
  }

  isUpdateImediately(currentVersion, selectedVersion) {
    // CASE 1:   - currentVersion and selectedVersion only differ by patch number
    return (
      currentVersion.major === selectedVersion.major &&
      currentVersion.minor === selectedVersion.minor &&
      currentVersion.patch !== selectedVersion.patch
    );
  }

  isDowngradeAndDropProperties(currentVersion, selectedVersion) {
    //  CASE 2:   - currentVersion and selectedVersion differ by minor version
    //              currentVersion > selectedVersion
    return (
      currentVersion.major === selectedVersion.major &&
      currentVersion.minor > selectedVersion.minor
    );
  }

  isShowEditModal(currentVersion, selectedVersion) {
    // CASE 3:   - currentVersion and selectedVersion differ by minor version
    //             currentVersion < selectedVersion
    //           - currentVersion and selectedVersion differ by major version
    return (
      (currentVersion.major === selectedVersion.major &&
        currentVersion.minor < selectedVersion.minor) ||
      currentVersion.major !== selectedVersion.major
    );
  }

  downgradeVersion(metaData, widgetDetails) {
    const members = widgetDetails.manifest.modules[0].declarations[0].members;
    const filteredMembers = this.filterMembers(members);
    const properties = {};
    for (const property of filteredMembers) {
      properties[property.name] = metaData.properties[property.name];
    }
    metaData.properties = properties;
  }

  toWrapperTagName(name) {
    return `${name}-wrapper`;
  }

  defineCustomComponent(manifest, classContent) {
    try {
      const customTagName = manifest.modules[0].declarations[0].tagName;
      const wrapperTagName = this.toWrapperTagName(customTagName);
      window.defineCDEWrapper(wrapperTagName, customTagName, classContent);
    } catch (error) {
      console.error("Error creating custom web component from manifest", error);
    }
  }
}

export default new DistributionChannelUtil();
